import { useState } from "react";
import { Send } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/hooks/useAuth";
import type { HelpComment } from "@/types/help";
import { initials } from "./ArticleCard";

function formatCommentTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}, ${pad(d.getDate())}.${pad(
    d.getMonth() + 1,
  )}.${d.getFullYear()}`;
}

function CommentCard({ comment }: { comment: HelpComment }) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-(--gray-200) bg-(--gray-100) p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Avatar className="size-12 rounded-lg border border-(--gray-200)">
            <AvatarImage src={comment.author.avatarUrl} alt="" />
            <AvatarFallback className="rounded-lg text-sm">
              {initials(comment.author.name)}
            </AvatarFallback>
          </Avatar>
          <div className="leading-tight">
            <p className="text-base font-medium text-(--color-text-primary)">
              {comment.author.name}
            </p>
            <p className="text-sm text-(--gray-500)">{comment.author.email}</p>
          </div>
        </div>
        <p className="shrink-0 text-sm font-medium text-(--gray-500)">
          {formatCommentTime(comment.createdAt)}
        </p>
      </div>
      <p className="text-sm leading-relaxed text-(--color-text-primary)">{comment.body}</p>
    </div>
  );
}

export function ArticleComments({
  comments: initialComments,
}: {
  comments: HelpComment[];
}) {
  const { t } = useLingui();
  const { user } = useAuth();
  const [comments, setComments] = useState<HelpComment[]>(initialComments);
  const [draft, setDraft] = useState("");

  const displayName =
    `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() ||
    user?.email ||
    t`You`;

  const submit = () => {
    const body = draft.trim();
    if (!body) return;
    const next: HelpComment = {
      id: `local-${Date.now()}`,
      author: { name: displayName, email: user?.email ?? "" },
      createdAt: new Date().toISOString(),
      body,
    };
    setComments((prev) => [next, ...prev]);
    setDraft("");
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-medium text-(--color-text-primary)">{t`Comments`}</h2>
          <span className="flex h-8 items-center justify-center rounded-full bg-(--gray-100) px-4 text-base text-(--gray-600)">
            {comments.length}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <Avatar className="size-12 rounded-lg border border-(--gray-200)">
            <AvatarFallback className="rounded-lg text-sm">
              {initials(displayName)}
            </AvatarFallback>
          </Avatar>
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
            placeholder={t`Start typing...`}
            aria-label={t`Add a comment`}
            className="h-12 flex-1 rounded-[0.625rem] border-(--shell-border) bg-(--color-surface-page) shadow-(--shell-shadow)"
          />
          <Button
            className="h-12 rounded-[0.625rem]"
            onClick={submit}
            disabled={!draft.trim()}
          >
            <Send className="size-6" />
            {t`Send`}
          </Button>
        </div>
      </div>

      <Separator className="bg-(--gray-200)" />

      <div className="flex flex-col gap-3">
        {comments.map((comment) => (
          <CommentCard key={comment.id} comment={comment} />
        ))}
      </div>
    </div>
  );
}
